import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Badge,
  Alert,
  Table,
  Container
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaCog,
  FaArrowLeft
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import AppToast from '../AppToast';
import FieldDependencyForm from './FieldDependencyForm';
import DependentValueManager from './DependentValueManager';
import './css/FieldManager.css';

const DependentFieldManager = ({ objectName, objectId, onBack, fields, onNavigate }) => {
  const [dependencies, setDependencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showForm, setShowForm] = useState(false);
  const [selectedDependency, setSelectedDependency] = useState(null);

  // Debug logging
  console.log('DependentFieldManager - objectId received:', objectId);
  console.log('DependentFieldManager - objectId type:', typeof objectId);
  console.log('DependentFieldManager - objectId length:', objectId ? objectId.length : 'undefined');

  // Toast helper functions
  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  // Filter picklist fields from the passed fields
  const picklistFields = fields && fields.length > 0 ? fields.filter(field =>
    field.data_type === 'picklist' || field.data_type === 'multipicklist'
  ) : [];

  // Debug logging
  console.log('DependentFieldManagerPage - fields received:', fields);
  console.log('DependentFieldManagerPage - fields type:', typeof fields);
  console.log('DependentFieldManagerPage - fields length:', fields ? fields.length : 'undefined');
  console.log('DependentFieldManagerPage - picklistFields filtered:', picklistFields);

  // Fetch dependencies when component mounts
  useEffect(() => {
    console.log('DependentFieldManager - objectId received in useEffect:', objectId);
    if (objectId) {
      fetchDependencies();
    }
  }, [objectId]);

  const fetchDependencies = async () => {
    console.log('DependentFieldManager - objectId received in fetchDependencies:', objectId);
    try {
      const dataApi = new DataApi('picklist/field-dependencies');
      const response = await dataApi.fetchById(objectId);

      if (response.data.success) {
        console.log('DependentFieldManager - dependencies received:', response.data.data);
        setDependencies(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching dependencies:', error);
      showToastMessage('Failed to fetch dependencies', 'danger');
    }
  };

  const handleCreateDependency = () => {
    setShowForm(true);
  };

  const handleDependencyCreated = () => {
    fetchDependencies(); // Refresh the list
  };

  const handleDeleteDependency = async (id) => {
    if (window.confirm('Are you sure you want to delete this dependency?')) {
      try {
        const dataApi = new DataApi('picklist/field-dependencies');
        const response = await dataApi.delete(id);
        if (response.data.success) {
          showToastMessage('Dependency deleted successfully!', 'success');
          fetchDependencies(); // Refresh the list
        } else {
          showToastMessage(response.data.message || 'Failed to delete dependency', 'danger');
        }
      } catch (error) {
        console.error('Error deleting dependency:', error);
        showToastMessage('Failed to delete dependency', 'danger');
      }
    }
  };

  const getFieldLabel = (fieldId) => {
    const field = picklistFields.find(f => f.id === fieldId);
    return field ? field.field_label : fieldId;
  };

  const getFieldName = (fieldId) => {
    const field = picklistFields.find(f => f.id === fieldId);
    return field ? field.field_name : fieldId;
  };

  const getFieldType = (fieldId) => {
    const field = picklistFields.find(f => f.id === fieldId);
    return field ? field.data_type : '';
  };





  // Show value manager if a dependency is selected
  if (selectedDependency) {
    return (
      <DependentValueManager
        objectName={objectName}
        objectId={objectId}
        fields={fields}
        dependencies={[selectedDependency]} // Pass only the selected dependency
        onBack={() => setSelectedDependency(null)}
        onNavigate={onNavigate}
        onGoBack={() => setSelectedDependency(null)}
        currentPageTitle={`Configure Values - ${getFieldLabel(selectedDependency.controlling_field)} → ${getFieldLabel(selectedDependency.dependent_field)}`}
      />
    );
  }

  return (
    <Container fluid>
      {/* Header */}
      <div className="mb-4">
        {/* Top Back Button - Always Visible */}
        <div className="mb-3">
          <Button
            variant="outline-secondary"
            onClick={onBack}
          >
            <FaArrowLeft className="me-1" />
            Back to Field Manager
          </Button>
        </div>

        {/* Main Header Content */}
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">Dependent Picklist Manager</h2>
            <p className="text-muted mb-0">Object: {objectName}</p>
          </div>
          <Button variant="primary" onClick={handleCreateDependency}>
            <FaPlus className="me-1" />
            Create Field Dependency
          </Button>
        </div>
      </div>

      {/* AppToast for notifications */}
      <AppToast
        show={showToast}
        message={toastMessage}
        variant={toastVariant}
        onClose={handleToastClose}
      />

      {/* Main Content */}
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="mb-1">Manage dependent picklist field relationships</h6>
              <p className="text-muted mb-0">
                Configure which picklist fields depend on other picklist fields. Note: A dependency can only exist in one direction between any two fields.
              </p>
              {!fields || fields.length === 0 ? (
                <Alert variant="danger" className="mt-2">
                  <strong>No fields received!</strong> Fields data is not available. Please check the console for debugging information.
                </Alert>
              ) : picklistFields.length === 0 ? (
                <Alert variant="warning" className="mt-2">
                  <strong>No picklist fields found!</strong> Make sure you have fields with data type 'picklist' or 'multipicklist' in this object.
                </Alert>
              ) : null}
            </div>
          </div>

          {!fields || fields.length === 0 ? (
            <div className="text-center py-4">
              <Alert variant="danger">
                <strong>Fields not available!</strong>
                <br />
                The fields data has not been loaded. Please return to Field Manager and try again.
              </Alert>
            </div>
          ) : loading ? (
            <div className="text-center py-4">
              <div className="loading-spinner mb-3"></div>
              <p>Loading dependencies...</p>
            </div>
          ) : dependencies.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No field dependencies configured yet.</p>
              <p className="text-muted">Click "Create Field Dependency" to create your first dependent picklist relationship.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Controlling Field</th>
                    <th>Dependent Field</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th style={{ width: 200 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dependencies.map((dependency) => (
                    <tr key={dependency.id}>
                      <td>
                        <div>
                          <strong>{getFieldLabel(dependency.controlling_field)}</strong>
                          <br />
                          <small className="text-muted">
                            {getFieldName(dependency.controlling_field)} - {getFieldType(dependency.controlling_field)}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{getFieldLabel(dependency.dependent_field)}</strong>
                          <br />
                          <small className="text-muted">
                            {getFieldName(dependency.dependent_field)} - {getFieldType(dependency.dependent_field)}
                          </small>
                        </div>
                      </td>
                      <td>
                        <Badge bg={dependency.is_active ? 'success' : 'secondary'}>
                          {dependency.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        {dependency.createddate ? new Date(dependency.createddate).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setSelectedDependency(dependency)}
                            title="Configure dependent values for this field relationship"
                          >
                            <FaCog className="me-1" />
                            Configure Values
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteDependency(dependency.id)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Field Dependency Form */}
      <FieldDependencyForm
        show={showForm}
        onHide={() => setShowForm(false)}
        fields={fields}
        objectName={objectName}
        objectId={objectId}
        existingDependencies={dependencies}
        onSuccess={handleDependencyCreated}
      />
    </Container>
  );
};

export default DependentFieldManager;
